import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { TableTopPanel } from 'components/TableTopPanel';
import type {
    ContractStatusEnum,
    WorkOrderObjectData,
    WorkOrderStatusEnum,
    WorkOrderVersionSeasonData,
} from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';

import { forwardRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { getUerWorkOrderRowClass } from '../../WorkOrder/WorkOrderTable/helpers';
import { useWorkOrderVersionColumns } from '../hooks/useWorkOrderVersionColumns';
import { useWorkOrderVersionData } from '../hooks/useWorkOrderVersionData';

import { useWorkOrderVersionActs } from './useWorkOrderVersionActs';

interface IWorkOrderTableProps {
    workOrderId: number;
    objectId: number | undefined;
    subObjectId: number | undefined;
    constructionTypeId: number;
    workOrderHasInProgress: boolean;
    workOrderStatus: WorkOrderStatusEnum | undefined;
    costRounding: number;
    contractStatus: ContractStatusEnum | undefined;
    isDisabledByProjectStatus: boolean;
    reloadWorkOrderTable: () => void;
    refreshProjectData?: () => void;
    versionId: number;
    seasons: WorkOrderVersionSeasonData[];
    workOrderObjects: WorkOrderObjectData[] | null | undefined;
}

export const WorkOrderVersionTable = forwardRef((props: IWorkOrderTableProps, ref: any) => {
    const {
        workOrderId,
        objectId,
        subObjectId,
        costRounding,
        constructionTypeId,
        workOrderStatus,
        workOrderHasInProgress,
        contractStatus,
        isDisabledByProjectStatus,
        reloadWorkOrderTable,
        versionId,
        workOrderObjects,
        seasons,
    } = props;
    const { id: projectId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const hasWorkKind = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasWorkKind,
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasWorkKind],
    );
    const tableKey = useMemo(
        () => `${workOrderId}_${constructionTypeId}_${objectId}_${subObjectId}_${versionId}`,
        [constructionTypeId, objectId, subObjectId, versionId, workOrderId],
    );

    const { acts } = useWorkOrderVersionActs({
        versionId,
    });

    const { workOrderPriceData, fetchedItems, totalItems, changeFilterHandler } = useWorkOrderVersionData({
        constructionTypeId,
        objectId,
        subObjectId,
        versionId,
    });

    const { columns, isColumnVisible, fullSetSettingsColumns, tableSettingsConfirm } = useWorkOrderVersionColumns({
        workOrderHasInProgress,
        workOrderStatus,
        costRounding,
        seasons,
        contractStatus,
        isDisabledByProjectStatus,
        refreshPriceListData: reloadWorkOrderTable,
        workOrderActs: acts,
        changeFilterHandler,
        hasWorkKind,
        workOrderObjects,
        versionId,
        constructionTypeId,
        objectId,
        subObjectId,
    });

    const tableRowClassGetter = useMemo(() => getUerWorkOrderRowClass(workOrderStatus), [workOrderStatus]);
    const isTableRdy = useMemo(() => !!(workOrderId && constructionTypeId), [constructionTypeId, workOrderId]);

    return (
        <>
            <TableTopPanel
                fetchedItems={fetchedItems}
                totalItems={totalItems}
                isColumnVisible={isColumnVisible}
                optionalColumns={fullSetSettingsColumns}
                handleTableSettingsConfirm={tableSettingsConfirm}
                showPinColumn
                tableApi={ref?.current}
            />
            <div className="ag-theme-alpine workOrderPrice">
                {isTableRdy && (
                    <AgGridTable
                        ref={ref}
                        datasource={workOrderPriceData}
                        columnDefs={columns}
                        rowModelType="infinite"
                        maxConcurrentDatasourceRequests={1}
                        maxBlocksInCache={50}
                        cacheBlockSize={50}
                        rowClassRules={tableRowClassGetter}
                        context={{
                            tableUniqueId: tableKey,
                        }}
                        defaultColDef={{
                            sortable: false,
                        }}
                        key={tableKey}
                    />
                )}
            </div>
        </>
    );
});

WorkOrderVersionTable.displayName = 'WorkOrderVersionTable';
